import { Navigate } from "react-router";

import { useFlags } from "launchdarkly-react-client-sdk";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";

import { themeClasses, C } from "sj-ui-toolkit/src/atlas/utils";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";

import {
  useAspenChat,
  ChatInput,
  ChatSessionSidebar,
  ChatSuggestions,
  COMMON_SUGGESTIONS,
} from "sj-ui-toolkit/src/atlas/components/AspenChat";

import { ChatMessageList } from "./ChatMessageList";
import S from "./AspenChatPage.module.scss";

export default function AspenChatPage() {
  const { aspenTenantAdminChat } = useFlags();
  const { isTenantAdmin } = useUserContext();

  if (!aspenTenantAdminChat || !isTenantAdmin) {
    return <Navigate to="/" replace />;
  }

  return <AspenChatPanel />;
}

function AspenChatPanel() {
  const {
    selectedTenant,
    sessionId,
    sessions,
    sessionsLoading,
    sessionsError,
    sessionsHasMore,
    transcriptLoading,
    messages,
    input,
    setInput,
    streaming,
    error,
    pendingSelection,
    setPendingSelection,
    hasMessages,
    sendMessage,
    stopStreaming,
    selectSession,
    handleNewChat,
  } = useAspenChat();

  return (
    <>
      <ConfirmDialog />

      <div className="flex h-[calc(100vh-64px)]">
        <ChatSessionSidebar
          sessions={sessions}
          selectedSessionId={sessionId}
          loading={sessionsLoading}
          error={sessionsError}
          hasMore={sessionsHasMore}
          streaming={streaming}
          onNewChat={handleNewChat}
          onSelectSession={selectSession}
        />

        {!hasMessages ? (
          <div className={S.emptyMessageContainer}>
            <div className="mb-12">
              <h1 className="atlas-h2 mb-4 text-center">Welcome to Aspen</h1>
              <div className="flex justify-center lg:hidden">
                <Button
                  label="New chat"
                  icon="pi pi-plus"
                  size="small"
                  onClick={handleNewChat}
                  disabled={streaming}
                  aria-label="Start new chat"
                />
              </div>
            </div>

            <ChatInput
              input={input}
              streaming={streaming}
              onInput={setInput}
              onSend={sendMessage}
              onStop={stopStreaming}
              pendingSelection={pendingSelection}
              onSelectionApplied={() => setPendingSelection(null)}
            />

            <ChatSuggestions
              suggestions={COMMON_SUGGESTIONS}
              tenant={selectedTenant}
              sendMessage={sendMessage}
              setInput={setInput}
              setPendingSelection={setPendingSelection}
            />

            <p className="text-xs text-surface-400 text-center">
              Aspen can make mistakes. Verify important information before acting on it.
            </p>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-w-0">
            <div
              className={C(
                themeClasses.bgElevation1,
                "lg:hidden w-full p-3 flex justify-end border-b border-surface-200"
              )}
            >
              <Button
                label="New chat"
                icon="pi pi-plus"
                size="small"
                onClick={handleNewChat}
                disabled={streaming}
                aria-label="Reset conversation"
              />
            </div>

            <ChatMessageList
              messages={messages}
              transcriptLoading={transcriptLoading}
              error={error}
              streaming={streaming}
            />

            <div className="sticky bottom-0 py-3">
              <div className="w-full max-w-4xl mx-auto px-4">
                <ChatInput
                  input={input}
                  streaming={streaming}
                  onInput={setInput}
                  onSend={sendMessage}
                  onStop={stopStreaming}
                />
                <p className="text-xs text-surface-400 text-center mt-2">
                  Aspen can make mistakes. Verify important information before acting on it.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
